import {useState} from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  FormLayout,
  IndexTable,
  InlineStack,
  Modal,
  Page,
  Select,
  Text,
  TextField,
} from '@shopify/polaris';
import type {PagePropsBase} from '@slj/ui/document';
import {AppFrame} from '@slj/ui/app-frame';

import type {Invite, InviteStatus} from '../invites';
import {joinUrl} from '../invites';
import type {ChromeProps} from './dashboard';
import {identityAccount, identitySections} from './nav';

export interface AdminUser {
  id: string;
  name: string;
  email: string;
  role: string | null;
  banned: number | null;
  createdAt: string;
}

export interface AdminProps extends PagePropsBase {
  chrome: ChromeProps;
  origin: string;
  users: AdminUser[];
  /** `expiresAt` arrives pre-formatted for display and `status` pre-computed —
   *  neither can be re-derived in render (see `inviteStatus`). */
  invites: (Invite & {status: InviteStatus})[];
  currentUserId: string;
  error?: string;
  justCreated?: Invite;
}

const errorMessage = (code: string) =>
  ({
    'self-role': "You can't change your own role.",
    'self-ban': "You can't ban yourself.",
  })[code] ?? 'Something went wrong.';

/** The status is decided server-side (`inviteStatus`) — render must not call
 *  `Date.now()`, and `expiresAt` has already been formatted for display by
 *  then, so its year is gone. This only picks the tone. */
const inviteTone = (status: InviteStatus): 'success' | undefined =>
  status === 'open' ? 'success' : undefined;

/** Hydrated copy-to-clipboard with the same label-swap feedback as before. */
function CopyButton({text, label = 'Copy'}: {text: string; label?: string}) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      size="slim"
      onClick={async () => {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
    >
      {copied ? 'Copied' : label}
    </Button>
  );
}

export function AdminPage({chrome, origin, users, invites, currentUserId, error, justCreated}: AdminProps) {
  const {user} = chrome;
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('user');
  const [roleTarget, setRoleTarget] = useState<AdminUser | null>(null);
  const [banTarget, setBanTarget] = useState<AdminUser | null>(null);

  return (
    <AppFrame
      logoUrl="/assets/logo.svg"
      user={user}
      sections={identitySections(user.isAdmin)}
      currentPath={chrome.path}
      account={identityAccount}
    >
      <Page title="Admin" fullWidth>
        <BlockStack gap="500">
          {error ? (
            <Banner tone="critical">
              <p>{errorMessage(error)}</p>
            </Banner>
          ) : null}

          {justCreated ? (
            <Banner tone="success">
              <InlineStack align="space-between" blockAlign="center" wrap gap="200">
                <code style={{wordBreak: 'break-all'}}>{joinUrl(origin, justCreated.code)}</code>
                <CopyButton text={joinUrl(origin, justCreated.code)} label="Copy link" />
              </InlineStack>
            </Banner>
          ) : null}

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Invites
              </Text>
              <Text as="p" tone="subdued">
                Mint a link, send it to a friend. Signup is impossible without one.
              </Text>
              <form method="post" action="/admin/invites">
                <FormLayout>
                  <InlineStack gap="300" wrap={false}>
                    <div style={{flexGrow: 1}}>
                      <TextField
                        label="Email (optional)"
                        type="email"
                        id="email"
                        name="email"
                        autoComplete="off"
                        placeholder="friend@example.com"
                        value={email}
                        onChange={setEmail}
                      />
                    </div>
                    <div style={{width: '8rem'}}>
                      <Select
                        label="Role"
                        id="role"
                        name="role"
                        options={[
                          {label: 'User', value: 'user'},
                          {label: 'Admin', value: 'admin'},
                        ]}
                        value={role}
                        onChange={setRole}
                      />
                    </div>
                  </InlineStack>
                  <InlineStack>
                    <Button submit variant="primary">
                      Create invite
                    </Button>
                  </InlineStack>
                </FormLayout>
              </form>
              <Text as="p" variant="bodySm" tone="subdued">
                Leaving email blank makes the invite usable by whoever opens the link. Setting it
                locks the invite to that address.
              </Text>
            </BlockStack>
          </Card>

          <Card padding="0">
            <Box padding="400" paddingBlockEnd="0">
              <Text as="h2" variant="headingMd">
                Outstanding &amp; recent
              </Text>
            </Box>
            {invites.length === 0 ? (
              <Box padding="400">
                <Text as="p" tone="subdued">
                  No invites yet.
                </Text>
              </Box>
            ) : (
              <IndexTable
                resourceName={{singular: 'invite', plural: 'invites'}}
                itemCount={invites.length}
                selectable={false}
                headings={[{title: 'Code'}, {title: 'For'}, {title: 'Role'}, {title: 'Status'}, {title: 'Expires'}, {title: ''}]}
              >
                {invites.map((inv, index) => (
                  <IndexTable.Row id={inv.code} key={inv.code} position={index}>
                    <IndexTable.Cell>
                      <code>{inv.code}</code>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      {inv.email ?? <Text as="span" tone="subdued">anyone</Text>}
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      {inv.role === 'admin' ? <Badge tone="attention">admin</Badge> : <Badge>user</Badge>}
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Badge tone={inviteTone(inv.status)}>{inv.status}</Badge>
                    </IndexTable.Cell>
                    <IndexTable.Cell>{inv.expiresAt}</IndexTable.Cell>
                    <IndexTable.Cell>
                      {!inv.redeemedAt ? (
                        <InlineStack gap="200" align="end" wrap={false}>
                          <CopyButton text={joinUrl(origin, inv.code)} />
                          <form method="post" action="/admin/invites/revoke">
                            <input type="hidden" name="code" value={inv.code} />
                            <Button submit size="slim">
                              Revoke
                            </Button>
                          </form>
                        </InlineStack>
                      ) : null}
                    </IndexTable.Cell>
                  </IndexTable.Row>
                ))}
              </IndexTable>
            )}
          </Card>

          <Card padding="0">
            <Box padding="400" paddingBlockEnd="0">
              <Text as="h2" variant="headingMd">
                People ({users.length})
              </Text>
            </Box>
            <IndexTable
              resourceName={{singular: 'person', plural: 'people'}}
              itemCount={users.length}
              selectable={false}
              headings={[{title: 'Name'}, {title: 'Email'}, {title: 'Role'}, {title: 'Joined'}, {title: ''}]}
            >
              {users.map((u, index) => {
                const isSelf = u.id === currentUserId;
                const isAdmin = u.role === 'admin';
                const isBanned = !!u.banned;
                return (
                  <IndexTable.Row id={u.id} key={u.id} position={index}>
                    <IndexTable.Cell>
                      <Text as="span" fontWeight="medium">
                        {u.name}
                      </Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text as="span" tone="subdued">{u.email}</Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <InlineStack gap="200" wrap={false}>
                        {isAdmin ? <Badge tone="attention">admin</Badge> : <Badge>user</Badge>}
                        {isBanned ? <Badge tone="critical">banned</Badge> : null}
                      </InlineStack>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text as="span" tone="subdued">{u.createdAt}</Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <InlineStack gap="200" align="end" wrap={false}>
                        {/* Admins reach every app by role; the access page still opens,
                            but it explains that rather than offering toggles. */}
                        <Button size="slim" url={`/admin/users/${u.id}/access`}>
                          Access
                        </Button>
                        {isSelf ? (
                          <Text as="span" tone="subdued">you</Text>
                        ) : (
                          <>
                            <Button size="slim" onClick={() => setRoleTarget(u)}>
                              {isAdmin ? 'Make user' : 'Make admin'}
                            </Button>
                            <Button size="slim" tone={isBanned ? undefined : 'critical'} onClick={() => setBanTarget(u)}>
                              {isBanned ? 'Unban' : 'Ban'}
                            </Button>
                          </>
                        )}
                      </InlineStack>
                    </IndexTable.Cell>
                  </IndexTable.Row>
                );
              })}
            </IndexTable>
          </Card>
        </BlockStack>
      </Page>

      {/* Role change confirm — the target is force-signed-out by the handler, which is
          why this is a confirm and not a one-click action. Forms live inside the modal
          so the native POST survives the portal. */}
      {roleTarget ? (
        <Modal
          open
          onClose={() => setRoleTarget(null)}
          title={`Make ${roleTarget.name} ${roleTarget.role === 'admin' ? 'a user' : 'an admin'}?`}
        >
          <Modal.Section>
            <form method="post" action="/admin/users/role">
              <input type="hidden" name="userId" value={roleTarget.id} />
              <input type="hidden" name="role" value={roleTarget.role === 'admin' ? 'user' : 'admin'} />
              <BlockStack gap="400">
                <Text as="p">
                  Their sessions are revoked immediately — they sign in again to pick up the new
                  role.
                </Text>
                <InlineStack align="end" gap="200">
                  <Button onClick={() => setRoleTarget(null)}>Cancel</Button>
                  <Button submit variant="primary">
                    Confirm
                  </Button>
                </InlineStack>
              </BlockStack>
            </form>
          </Modal.Section>
        </Modal>
      ) : null}

      {banTarget ? (
        <Modal
          open
          onClose={() => setBanTarget(null)}
          title={`${banTarget.banned ? 'Unban' : 'Ban'} ${banTarget.name}?`}
        >
          <Modal.Section>
            <form method="post" action="/admin/users/ban">
              <input type="hidden" name="userId" value={banTarget.id} />
              <input type="hidden" name="unban" value={banTarget.banned ? '1' : '0'} />
              <BlockStack gap="400">
                <Text as="p">
                  {banTarget.banned
                    ? 'They can sign in again immediately.'
                    : 'Their sessions are deleted and they can no longer sign in.'}
                </Text>
                <InlineStack align="end" gap="200">
                  <Button onClick={() => setBanTarget(null)}>Cancel</Button>
                  <Button submit tone={banTarget.banned ? undefined : 'critical'} variant="primary">
                    {banTarget.banned ? 'Unban' : 'Ban'}
                  </Button>
                </InlineStack>
              </BlockStack>
            </form>
          </Modal.Section>
        </Modal>
      ) : null}
    </AppFrame>
  );
}
